import { useMemo } from "react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { UIFlightData } from "@flights/types";
import { CardAncillaryConfigEnriched } from "@flights/search-ui";
import useGlobalContext from "../../../../../../hooks/useGlobalContext";
import { getLeastAvailableLuggagePerTrip } from "../../../../../../store/selectors/searchResultsAvailableLuggage";

import flights_web_sr_baggage_transparency from "../../../../../../utils/experiments/funnel/flights_web_sr_baggage_transparency";
import useUserAgent from "../../../../../../hooks/useUserAgent";
import useProfiler from "../../../../../../hooks/useProfiler";
import useGetSearchResultsBound from "../../../../../../hooks/useGetSearchResultsBound";

export const useFlightCardLuggageController = (
  flight: UIFlightData,
  isWithFlexTicket?: boolean,
  isFlightDetails?: boolean
) => {
  const { segments, ancillaries } = flight;
  const { supportsChildrenTickets } = useGlobalContext();
  const { isMobile } = useUserAgent();
  const i18n = useI18n();
  const profiler = useProfiler();
  const bound = useGetSearchResultsBound();

  const {
    personalItem: personalItemIncluded,
    cabinLuggage: cabinIncluded,
    checkedInLuggage: checkedIncluded
  } = getLeastAvailableLuggagePerTrip(bound === "outbound" ? [segments[0]] : segments, supportsChildrenTickets);

  const luggageList = useMemo(() => {
    const result: CardAncillaryConfigEnriched[] = [];

    const isBaggageTransparencyVariant = Boolean(
      flights_web_sr_baggage_transparency.getExp(isMobile).trackWithDefaultStage()
    );

    if (personalItemIncluded) {
      result.push({
        ancillaryType: "PERSONAL_ITEM",
        inclusionType: "INCLUDED",
        text: isBaggageTransparencyVariant
          ? i18n.trans(t("flights_sr_baggage_hover_personal_included"))
          : i18n.trans(t("flights_sr_hover_personal_item")),
        ariaLabel: isBaggageTransparencyVariant
          ? i18n.trans(t("flights_sr_baggage_hover_personal_included"))
          : i18n.trans(t("a11y_flights_mdot_sr_personal_included"))
      });
    }

    if (cabinIncluded) {
      result.push({
        ancillaryType: "CABIN",
        inclusionType: "INCLUDED",
        text: isBaggageTransparencyVariant
          ? i18n.trans(t("flights_sr_baggage_hover_cabin_included"))
          : i18n.trans(t("flights_sr_hover_cabin_bag")),
        ariaLabel: isBaggageTransparencyVariant
          ? i18n.trans(t("flights_sr_baggage_hover_cabin_included"))
          : i18n.trans(t("a11y_flights_mdot_sr_cabin_included"))
      });
    } else if (ancillaries?.cabinBaggagePerTraveller) {
      flights_web_sr_baggage_transparency.trackStages(isMobile, profiler);
      if (isFlightDetails) {
        flights_web_sr_baggage_transparency.getExp(isMobile).goals.fd_open_for_affected_offers();
      }

      if (isBaggageTransparencyVariant) {
        result.push({
          ancillaryType: "CABIN",
          inclusionType: "EXTRA",
          text: i18n.trans(t("flights_sr_baggage_hover_cabin_available")),
          ariaLabel: i18n.trans(t("a11y_flights_mdot_sr_icon_cabin_available"))
        });
      }
    }

    if (checkedIncluded) {
      result.push({
        ancillaryType: "CHECKED",
        inclusionType: "INCLUDED",
        text: isBaggageTransparencyVariant
          ? i18n.trans(t("flights_sr_baggage_hover_checked_included"))
          : i18n.trans(t("flights_sr_hover_checked_bag")),
        ariaLabel: isBaggageTransparencyVariant
          ? i18n.trans(t("flights_sr_baggage_hover_checked_included"))
          : i18n.trans(t("a11y_flights_mdot_sr_checked_included"))
      });
    } else if (ancillaries?.checkedInBaggage) {
      flights_web_sr_baggage_transparency.trackStages(isMobile, profiler);
      if (isFlightDetails) {
        flights_web_sr_baggage_transparency.getExp(isMobile).goals.fd_open_for_affected_offers();
      }

      if (isBaggageTransparencyVariant) {
        result.push({
          ancillaryType: "CHECKED",
          inclusionType: "EXTRA",
          text: i18n.trans(t("flights_sr_baggage_hover_checked_available")),
          ariaLabel: i18n.trans(t("a11y_flights_mdot_sr_icon_checked_available"))
        });
      }
    }

    if (isWithFlexTicket && ancillaries?.flexibleTicket) {
      result.push({
        ancillaryType: "FLEX_TICKET",
        inclusionType: "EXTRA",
        text: isBaggageTransparencyVariant
          ? i18n.trans(t("flights_sr_baggage_hover_flex_available"))
          : i18n.trans(t("flights_sr_hover_flex_ticket")),
        ariaLabel: isBaggageTransparencyVariant
          ? i18n.trans(t("a11y_flights_mdot_sr_icon_flex_available"))
          : i18n.trans(t("flights_sr_hover_flex_ticket"))
      });
    }

    return result;
  }, [
    ancillaries?.cabinBaggagePerTraveller,
    ancillaries?.checkedInBaggage,
    ancillaries?.flexibleTicket,
    cabinIncluded,
    checkedIncluded,
    i18n,
    isFlightDetails,
    isMobile,
    isWithFlexTicket,
    personalItemIncluded,
    profiler
  ]);

  return {
    luggageList
  };
};
